"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useRequestAudit } from "@/components/RequestAuditProvider";
import { socialLinks } from "@/lib/social-links";

type NavItem = { label: string; href: string; description: string; icon: string; external?: boolean };

/*
  The Services dropdown is a two-column mega-menu. "Audits & Assessments" is the
  code-security core (point-in-time engagements); "Operational Security" is the
  suite that defends the operation around the code, running continuously or on
  demand. Infrastructure pentest lives with the operational work it belongs to.
*/
const servicesAssess: readonly NavItem[] = [
  { label: "Smart Contract Audits", href: "/audits", description: "Expert web3 security assessments", icon: "/images/audits.svg" },
  { label: "Formal Verification", href: "/formal-verification", description: "Continuous assurance for systems that keep evolving", icon: "/images/formal-verification.svg" },
  { label: "Blitz", href: "https://blitz.quantstamp.com", description: "Accelerated security reviews for web3 projects", icon: "/images/blitz.svg", external: true },
  { label: "Economic Exploit Analysis", href: "/economic-exploits", description: "Analysis and mitigation of economic attacks", icon: "/images/exploit-analysis.svg" },
  { label: "Penetration Testing & Infra Audits", href: "/infrastructure-audits", description: "Web3 infrastructure configuration security", icon: "/images/infrastructure-audits.svg" },
  { label: "Insurance", href: "https://chainproof.co/", description: "World's first regulated smart contract insurer", icon: "/images/insurance.svg", external: true },
];

const servicesOperational: readonly NavItem[] = [
  { label: "OpSec Review", href: "/opsec-review", description: "Close operational gaps before they're exploited", icon: "/images/web-app-security.svg" },
  { label: "Monitor", href: "/monitor", description: "Continuous watch on data, identity & your stack", icon: "/images/microchip.svg" },
  { label: "Incident Response", href: "/incident-response", description: "Contain, eradicate, recover from active attacks", icon: "/images/AlertIcon.svg" },
  { label: "vCISO", href: "/vciso", description: "Senior security leadership on call for your team", icon: "/images/team.svg" },
  { label: "Certification", href: "/certification", description: "Third-party attestation of your security posture", icon: "/images/solid-sharp-check-shield-1.svg" },
  { label: "Communication Firewall", href: "https://commondefense.ai", description: "Cross-channel social-engineering defense", icon: "/images/cloud-word.svg", external: true },
];

const serviceGroups: readonly { heading: string; items: readonly NavItem[] }[] = [
  { heading: "Audits & Assessments", items: servicesAssess },
  { heading: "Operational Security", items: servicesOperational },
];

/* Guides is its own top-level menu; the Audit Readiness Guide lives here now. */
const guides: readonly NavItem[] = [
  { label: "Audit Readiness Guide", href: "/audit-readiness-guide", description: "Prepare your project for a security audit", icon: "/images/file-shield.svg" },
  { label: "Incident Response Guide", href: "/incident-response-guide", description: "The first 24 hours after a protocol exploit", icon: "/images/AlertIcon.svg" },
];

const company = [
  { label: "Team", href: "/team", description: "Meet the Quantstamp team", icon: "/images/team.svg" },
  { label: "Auditors", href: "/auditors", description: "Our Security Auditors", icon: "/images/auditors.png" },
  { label: "Media", href: "/media", description: "Media assets", icon: "/images/media.svg" },
] as const;

/*
  The narrow mobile drawer can't do side-by-side columns, so the two service
  groups render as stacked, labeled sections. Audit Reports and Blog aren't
  services, so they live under About alongside the company links.
*/
type MobileCompanyItem = { label: string; href: string; icon: string; external?: boolean };

const mobileCompany: readonly MobileCompanyItem[] = [
  { label: "Audit Reports", href: "https://certificate.quantstamp.com/", icon: "/images/audit-reports.svg", external: true },
  { label: "Blog", href: "/blog", icon: "/images/blog.svg" },
  { label: "Team", href: "/team", icon: "/images/team.svg" },
  { label: "Auditors", href: "/auditors", icon: "/images/auditors.png" },
  { label: "Media", href: "/media", icon: "/images/media.svg" },
];

const mobileLegal = [
  { label: "Terms and conditions", href: "/legal/terms" },
  { label: "Privacy policy", href: "/legal/privacy" },
] as const;

export type HeaderTone = "white" | "black";

/* One mobile drawer link, shared by the quick-links block and both service groups. */
function renderMobileLink(item: NavItem, onNavigate: () => void) {
  const content = (
    <>
      <img src={item.icon} alt="" />
      <span>
        {item.label}{item.external ? <img src="/images/arrow-up-right-from-square-1.svg" alt="" aria-hidden="true" className="mobile-navigation__external" /> : null}
        <small>{item.description}</small>
      </span>
    </>
  );
  return item.external ? (
    <a href={item.href} key={item.href} target="_blank" rel="noreferrer" onClick={onNavigate}>{content}</a>
  ) : (
    <Link href={item.href} key={item.href} onClick={onNavigate}>{content}</Link>
  );
}

export function SiteHeader({ tone = "black" }: { tone?: HeaderTone }) {
  const [open, setOpen] = useState(false);
  const { open: openRequestAudit } = useRequestAudit();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, []);

  return (
    <header id="top" className={`site-header site-header--${tone}`}>
      <div className="site-header__inner">
        <Link href="/" aria-label="Quantstamp home" className="site-header__logo-link">
          <img src={tone === "white" ? "/images/white-quantstamp-nav-logo.svg" : "/images/quantstamp-logo-black.svg"} alt="Quantstamp" className="site-header__logo" />
        </Link>
        <nav className="site-nav" aria-label="Primary navigation">
          <div className="site-nav__dropdown">
            <button type="button" className="site-nav__trigger" aria-haspopup="true">Services <span className="site-nav__chevron" aria-hidden="true" /></button>
            <div className="site-nav__panel site-nav__panel--mega">
              {serviceGroups.map((group) => (
                <div className="site-nav__panel-group" key={group.heading}>
                  <p className="site-nav__panel-heading">{group.heading}</p>
                  {group.items.map((item) => item.external ? (
                    <a href={item.href} key={item.href} target="_blank" rel="noreferrer" className="site-nav__panel-link">
                      <img src={item.icon} alt="" />
                      <span className="site-nav__panel-copy">{item.label}<img src="/images/arrow-up-right-from-square-1.svg" alt="" aria-hidden="true" className="site-nav__external" /><small>{item.description}</small></span>
                    </a>
                  ) : (
                    <Link href={item.href} key={item.href} className="site-nav__panel-link">
                      <img src={item.icon} alt="" />
                      <span className="site-nav__panel-copy">{item.label}<small>{item.description}</small></span>
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <a href="https://certificate.quantstamp.com/" target="_blank" rel="noreferrer">Audit Reports</a>
          <div className="site-nav__dropdown">
            <button type="button" className="site-nav__trigger" aria-haspopup="true">Guides <span className="site-nav__chevron" aria-hidden="true" /></button>
            <div className="site-nav__panel">
              {guides.map((item) => (
                <Link href={item.href} key={item.href} className="site-nav__panel-link">
                  <img src={item.icon} alt="" />
                  <span className="site-nav__panel-copy">{item.label}<small>{item.description}</small></span>
                </Link>
              ))}
            </div>
          </div>
          <div className="site-nav__dropdown">
            <button type="button" className="site-nav__trigger" aria-haspopup="true">About <span className="site-nav__chevron" aria-hidden="true" /></button>
            <div className="site-nav__panel">
              {company.map((item) => (
                <Link href={item.href} key={item.href} className="site-nav__panel-link">
                  <img src={item.icon} alt="" />
                  <span className="site-nav__panel-copy">{item.label}<small>{item.description}</small></span>
                </Link>
              ))}
            </div>
          </div>
          <Link href="/blog">Blog</Link>
        </nav>
        <button type="button" className="site-header__cta" onClick={openRequestAudit}>
          <img src="/images/Button-1_1.svg" alt="" className="site-header__cta-img site-header__cta-img--default" />
          <img src="/images/668ff8a38f968cf1375f8d0a_Button-1.svg" alt="" className="site-header__cta-img site-header__cta-img--hover" />
          <span className="sr-only">Request a Security Audit</span>
        </button>
        <button
          type="button"
          className="site-header__menu"
          aria-expanded={open}
          aria-controls="mobile-navigation"
          aria-label={open ? "Close navigation" : "Open navigation"}
          onClick={() => setOpen((value) => !value)}
        >
          <img src={tone === "white" ? "/images/white-mobile-hamburger-button.svg" : "/images/black-hamburger.svg"} alt="" aria-hidden="true" />
        </button>
      </div>
      <div
        id="mobile-navigation"
        className={`mobile-navigation${open ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        aria-hidden={!open}
        onClick={() => setOpen(false)}
      >
        <div className="mobile-navigation__drawer" onClick={(event) => event.stopPropagation()}>
          <div className="mobile-navigation__header">
            <Link href="/" aria-label="Quantstamp home" onClick={() => setOpen(false)}>
              <img src="/images/color-mobile-nav-quantstamp-logo.svg" alt="Quantstamp" />
            </Link>
            <button type="button" className="mobile-navigation__close" aria-label="Close navigation" onClick={() => setOpen(false)}>
              <span aria-hidden="true" />
            </button>
          </div>

          <nav aria-label="Mobile navigation links">
            {serviceGroups.map((group) => (
              <div className="mobile-navigation__section" key={group.heading}>
                <h2>{group.heading}</h2>
                <div className="mobile-navigation__links">
                  {group.items.map((item) => renderMobileLink(item, () => setOpen(false)))}
                </div>
              </div>
            ))}

            <div className="mobile-navigation__section">
              <h2>Guides</h2>
              <div className="mobile-navigation__links">
                {guides.map((item) => renderMobileLink(item, () => setOpen(false)))}
              </div>
            </div>

            <div className="mobile-navigation__section">
              <h2>About</h2>
              <div className="mobile-navigation__links mobile-navigation__links--company">
                {mobileCompany.map((item) => item.external ? (
                  <a href={item.href} key={item.href} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>
                    <img src={item.icon} alt="" />
                    {item.label}
                  </a>
                ) : (
                  <Link href={item.href} key={item.href} onClick={() => setOpen(false)}>
                    <img src={item.icon} alt="" />
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mobile-navigation__section mobile-navigation__follow">
              <h2>Follow</h2>
              <div className="mobile-navigation__socials">
                {socialLinks.map((social) => (
                  <a href={social.href} key={social.label} target="_blank" rel="noreferrer" aria-label={`Quantstamp on ${social.label}`}>
                    <img src={social.darkIcon} alt="" />
                  </a>
                ))}
              </div>
            </div>

            <button type="button" className="mobile-navigation__cta" onClick={() => { setOpen(false); openRequestAudit(); }}>
              <span>Request a Security Audit</span>
              <img src="/images/white-up-chevron.svg" alt="" aria-hidden="true" />
            </button>

            <div className="mobile-navigation__legal">
              {mobileLegal.map((item) => (
                <Link href={item.href} key={item.href} onClick={() => setOpen(false)}>{item.label}</Link>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}
